import { maxBy, minBy } from 'es-toolkit';

import { AbsoluteVerticalCut, BearingEnvelope, PlaneCut, RoofPlane } from '@paragon/contract-library';
import {
  angleEquals,
  centerPoint3D,
  convertDegreesToRadians,
  Direction3D,
  distanceEquals,
  distanceGreaterThan,
  distanceLessThan,
  groupTransitivelyByDistanceEquality,
  Line3D,
  LineSegment2D,
  LineSegment3D,
  Plane3D,
  Point3D,
  Polygon3D,
  rightAngle,
} from '@paragon/geometry-library';
import { vertexLoopToPolygons } from '@paragon/geometry-library/source/scripts/polygonValidationUtilities';
import { Rotation } from '@paragon/geometry-library/source/scripts/rotation';
import {
  assertExhaustiveSwitch,
  circularPairwise,
  circularTripletwise,
  cycle,
  groupAdjacentElementsCircularly,
  groupTransitively,
  notNullish,
  ReadonlyRecord,
} from '@paragon/utilities';

import { getEnvelopeSegment } from './getEnvelopeSegment';
import { calculateTotalHeelHeight } from './heelSettings';
import {
  CalculatedRoofPlaneGeometry,
  DisplayablePlane,
  DisplayablePlaneWithCalculatedGeometry,
  isRoofPlane,
  PlaneWithCalculatedGeometry,
  RoofOrCeilingPlane,
} from './roof-plane-types';
import { createDisjointRoofBasePolygonFromEdges } from './roof-solids';
import { calculateBearingEnvelopeShape, getEnvelopeSegmentFromDefiningSegment } from './thickness-calculations';
import { RoofPlaneSide } from './truss-solids';

export function getPlaneWithGeometry<T extends RoofOrCeilingPlane>(
  plane: T,
  bearingEnvelopes: ReadonlyRecord<string, BearingEnvelope>,
): PlaneWithCalculatedGeometry<T> | null {
  const calculatedGeometry = calculatePlaneGeometry(plane, bearingEnvelopes);
  return calculatedGeometry ? { calculatedGeometry, ...plane } : null;
}

/**
 * Note that this function does not account for cantilevers, whereas `shiftCalculatedGeometryToAccountForCantilever`
 *   does (and works on relative planes as well). Do not use this function unless you are aware of and desire this
 *   behavior.
 */
export function calculateAbsoluteVerticalCutPlaneGeometry(roofPlane: DisplayablePlane): CalculatedRoofPlaneGeometry {
  if (roofPlane.geometry.type !== 'Absolute') {
    throw new Error(`calculateAbsoluteVerticalCutPlaneGeometry called on non-absolute plane`);
  }

  let segment = LineSegment2D.fromLineSegmentObject(roofPlane.geometry.segment);
  if (roofPlane.flipped) {
    segment = segment.reverse();
  }

  return {
    line: segment.toLine(),
    segment: roofPlane.geometry.isInfinite ? undefined : segment,

    elevation: roofPlane.geometry.elevation,
  };
}

/**
 * Note that this function does not account for cantilevers, whereas `shiftCalculatedGeometryToAccountForCantilever`
 *   does (and works on absolute planes as well). Do not use this function unless you are aware of and desire this
 *   behavior.
 */
export function calculateBearingEnvelopeBasedPlaneGeometry(
  plane: RoofOrCeilingPlane,
  bearingEnvelopes: Record<string, BearingEnvelope>,
): CalculatedRoofPlaneGeometry | null {
  if (plane.geometry.type !== 'BearingEnvelope') {
    throw new Error(`calculateBearingEnvelopeBasedPlaneGeometry called on non-bearing envelope plane`);
  }

  const bearingEnvelope = bearingEnvelopes[plane.geometry.bearingEnvelopeGuid];
  if (bearingEnvelope == null) return null;

  const envelopeDefiningSegment = getEnvelopeSegment(bearingEnvelope);
  if (envelopeDefiningSegment == null) return null;

  const justification = plane.flipped === isRoofPlane(plane) ? 'Back' : 'Front';
  let segment = getEnvelopeSegmentFromDefiningSegment(envelopeDefiningSegment, bearingEnvelope, justification);
  if (plane.flipped) segment = segment.reverse();

  return {
    segment,
    line: segment.toLine(),
    elevation: bearingEnvelope.top,
  };
}

function shiftCalculatedGeometryToAccountForCantilever(
  geometry: CalculatedRoofPlaneGeometry,
  plane: RoofOrCeilingPlane,
): CalculatedRoofPlaneGeometry {
  if (!isRoofPlane(plane) || plane.cantilever === 0) return geometry;

  let { segment, line } = geometry;

  const translation = line.direction.rotateReverseQuarterTurn().toVector(plane.cantilever);

  if (segment) segment = segment.translate(translation);
  line = line.translate(translation);

  return { segment, line, elevation: geometry.elevation };
}

export function calculatePlaneGeometry(
  plane: RoofOrCeilingPlane,
  bearingEnvelopes: Record<string, BearingEnvelope>,
): CalculatedRoofPlaneGeometry | null {
  switch (plane.geometry.type) {
    case 'Absolute':
      return shiftCalculatedGeometryToAccountForCantilever(calculateAbsoluteVerticalCutPlaneGeometry(plane), plane);

    case 'BearingEnvelope': {
      const geometry = calculateBearingEnvelopeBasedPlaneGeometry(plane, bearingEnvelopes);
      if (geometry == null) return null;

      return shiftCalculatedGeometryToAccountForCantilever(geometry, plane);
    }

    default:
      assertExhaustiveSwitch(plane.geometry);
  }
}

export function getTotalPlaneElevation(plane: DisplayablePlaneWithCalculatedGeometry) {
  const heelHeight = isRoofPlane(plane)
    ? (calculateTotalHeelHeight(plane.heelSettings, plane.topChordWidth, plane.slopeInDegrees) ?? 0)
    : 0;
  return plane.calculatedGeometry.elevation + heelHeight;
}

/**
 * Creates a plane that intersects the passed roof plane at its base line. For non-vertical planes, this will be a vertical
 * plane. For vertical planes, this will be a horizontal plane.
 */
export function calculateCutPlaneAtBaseLine(plane: DisplayablePlaneWithCalculatedGeometry) {
  if (angleEquals(plane.slopeInDegrees, 90)) {
    return Plane3D.fromPerpendicularDirectionAndPoint(
      Direction3D.out,
      plane.calculatedGeometry.line.basePoint.to3D(getTotalPlaneElevation(plane)),
    );
  }

  return calculateVerticalPlaneAtBaseLine(plane);
}

/** Gets the translation from the roof plane's base line to the overhang in 2D. */
export function getRoofPlaneOverhang2DTranslation(
  roofPlane: DisplayablePlaneWithCalculatedGeometry<RoofPlane>,
  { subtractSubfascia }: { subtractSubfascia: boolean },
) {
  const orientedBaseLine = roofPlane.calculatedGeometry.line;
  const offsetDirection = orientedBaseLine.direction.rotateReverseQuarterTurn();
  return offsetDirection.toVector(roofPlane.overhang - (subtractSubfascia ? roofPlane.subfasciaThickness : 0));
}

/** Gets the translation from the roof plane's base line to the overhang in 3D. */
export function getRoofPlaneOverhang3DTranslation(
  roofPlane: DisplayablePlaneWithCalculatedGeometry<RoofPlane>,
  { subtractSubfascia }: { subtractSubfascia: boolean },
) {
  const overhang2DTranslation = getRoofPlaneOverhang2DTranslation(roofPlane, { subtractSubfascia });
  const z = -(
    (roofPlane.overhang - (subtractSubfascia ? roofPlane.subfasciaThickness : 0)) *
    Math.tan(convertDegreesToRadians(roofPlane.slopeInDegrees))
  );
  return overhang2DTranslation.to3D(z);
}

export function calculateVerticalPlaneAtBaseLine(plane: DisplayablePlaneWithCalculatedGeometry) {
  const orientedBaseLine = plane.calculatedGeometry.line;
  const planeNormalDirection = orientedBaseLine.direction.rotateQuarterTurn().to3D()!;
  return Plane3D.fromPerpendicularDirectionAndPoint(planeNormalDirection, orientedBaseLine.basePoint.to3D());
}

export function absoluteVerticalCutToPlane(cut: AbsoluteVerticalCut) {
  const segment = LineSegment2D.fromLineSegmentObject(cut.segment);

  return Plane3D.fromPerpendicularDirectionAndPoint(
    segment.direction.rotateQuarterTurn().to3D()!,
    segment.midpoint.to3D(),
  );
}

export function planeCutToPlane<T extends RoofOrCeilingPlane>(
  cut: PlaneCut,
  allPlanes: ReadonlyRecord<string, PlaneWithCalculatedGeometry<T> | null>,
): Plane3D | null {
  switch (cut.type) {
    case 'AbsoluteVertical':
      return absoluteVerticalCutToPlane(cut);

    case 'AgainstPlane':
      const cuttingPlane = allPlanes[cut.cuttingPlaneGuid];
      return cuttingPlane ? convertPlaneToGeometricPlane(cuttingPlane, 'Bottom') : null;

    default:
      assertExhaustiveSwitch(cut);
  }
}

/** Gets the geometric plane in layout coordinates. */
export function convertPlaneToGeometricPlane(plane: DisplayablePlaneWithCalculatedGeometry, planeSide: RoofPlaneSide) {
  const orientedBaseLine = plane.calculatedGeometry.line;
  const basePoint = orientedBaseLine.basePoint
    .to3D()
    .translate(Direction3D.out.toVector(getTotalPlaneElevation(plane)));

  const orientationAngle = isRoofPlane(plane) ? rightAngle : -rightAngle;

  const xyNormalDirection = orientedBaseLine.direction.rotateQuarterTurn().to3D()!;
  const rotation = Rotation.fromAxisAndAngle(
    new Line3D(orientedBaseLine.direction.to3D()!, Point3D.origin),
    plane.slopeInDegrees + orientationAngle,
  );
  const normalDirection = xyNormalDirection.rotate(rotation);
  const geometricPlane = Plane3D.fromPerpendicularDirectionAndPoint(normalDirection, basePoint);
  return planeSide === 'Bottom' ||
    !isRoofPlane(plane) ||
    geometricPlane.halfSpace.direction.isPerpendicularTo(Direction3D.positiveZ)
    ? geometricPlane
    : geometricPlane.translate(
        geometricPlane.halfSpace.direction.orientWith(Direction3D.positiveZ).toVector(plane.deckingThickness),
      );
}

type PlaneEdgeData = { edge: LineSegment3D; overhang: OverhangType | null };
export type BoundedPlaneData = (
  | { inProgress: false; shape: readonly Polygon3D[] }
  | {
      inProgress: true;
      shape: {
        polygons: readonly Polygon3D[];
        segments: readonly LineSegment3D[];
        lines: readonly Line3D[];
        dashedSegments: readonly LineSegment3D[];
      };
    }
) & { edges: readonly PlaneEdgeData[] };
export type BoundedPlaneShape = BoundedPlaneData['shape'];

/**
 * Cuts a plane into a polygon by bounding it with a series of intersections against other planes. If unsuccessful,
 * attempts to return as many edges as could be calculated in a list, plus a start and end line if applicable.
 * @param cutPlanes The planes used to form the boundary, in order of their intersections.
 */
function cutPlaneByOrderedPlanesWithOverhangs(
  plane: Plane3D,
  cutPlanes: readonly PlaneCutWithOverhang[],
  inProgress: boolean,
): BoundedPlaneData {
  const intersectionsWithThisPlane = cutPlanes.map((planeWithOverhang) => {
    const intersectionLine = planeWithOverhang.plane ? plane.intersectWithPlane(planeWithOverhang.plane) : null;
    return intersectionLine && { intersectionLine, overhang: planeWithOverhang.overhang };
  });

  const pointIntersections = circularPairwise(intersectionsWithThisPlane)
    .map(([previous, next]) => {
      if (!previous || !next) return null;
      const intersection = previous.intersectionLine.intersectWithLine(next.intersectionLine);
      if (!intersection) return null;
      if (intersection instanceof Line3D) return 'drop';
      return { intersection, previous, next };
    })
    .filter((intersection): intersection is Exclude<typeof intersection, 'drop'> => intersection !== 'drop');

  const groupedIntersections = groupAdjacentElementsCircularly(
    pointIntersections,
    (point1, point2) => !!point1 && !!point2 && point1.intersection.equals(point2.intersection),
  ).map((group) => {
    if (group.every(notNullish)) {
      const point = centerPoint3D(group.map(({ intersection }) => intersection));
      return { point, previous: group[0].previous, next: group.at(-1)!.next };
    }
    return null;
  });

  let shape: readonly Polygon3D[] | undefined;
  let edges: readonly PlaneEdgeData[] | undefined;
  if (groupedIntersections.length > 2 && groupedIntersections.every(notNullish)) {
    try {
      const initialEdges = circularPairwise(groupedIntersections).map(([point1, point2]): PlaneEdgeData => ({
        edge: new LineSegment3D(point1.point, point2.point),
        overhang: point1.next.overhang === point2.previous.overhang ? point1.next.overhang : null,
      }));
      const initialShape = vertexLoopToPolygons(groupedIntersections.map(({ point }) => point));
      if (initialShape) {
        const reverse = !initialShape[0].isOrientedWith({ normalDirection: plane.halfSpace.direction });
        shape = reverse ? initialShape.map((polygon) => polygon.reverse()) : initialShape;
        edges = reverse
          ? initialEdges.map(({ edge, overhang }) => ({ edge: edge.reverse(), overhang })).toReversed()
          : initialEdges;
        if (!inProgress) return { inProgress, shape, edges };
      }
    } catch {
      // continue
    }
  }

  if (!inProgress) return { inProgress, shape: [], edges: [] };

  // Cycle so that intersection resulting from most recent cut is last
  const cycledGroupedIntersections = cycle(groupedIntersections, groupedIntersections.length - 1);

  if (
    cycledGroupedIntersections.some(
      (intersection, index) => intersection == null && index !== cycledGroupedIntersections.length - 1,
    )
  ) {
    return { inProgress: true, shape: { polygons: [], segments: [], lines: [], dashedSegments: [] }, edges: [] };
  }

  const segments = circularPairwise(groupedIntersections)
    .map(([previous, next]) =>
      previous && next && !previous.point.equals(next.point) ? new LineSegment3D(previous.point, next.point) : null,
    )
    .filter(notNullish);

  const firstLine =
    cycledGroupedIntersections.at(-1) == null || cycledGroupedIntersections.length === 1
      ? intersectionsWithThisPlane[0]?.intersectionLine
      : null;
  const lastLine = groupedIntersections.length === 1 ? intersectionsWithThisPlane.at(-1)?.intersectionLine : null;
  const lines = [firstLine, lastLine].filter(notNullish);

  const dashedSegments = [];
  if (groupedIntersections.length > 1) {
    // The "ray" is actually usually a long segment that may even rarely be trimmed to a short segment.
    const rayStart = (cycledGroupedIntersections.at(-1) ?? cycledGroupedIntersections.at(-2))!.point;
    const rayLine = intersectionsWithThisPlane.at(-1)!.intersectionLine;
    const RAY_LENGTH = 5000;
    const raySegmentIntersectionProjections = segments
      .map((segment) => {
        const linex2Intersection = rayLine.intersectWithLine(segment.toLine());
        return linex2Intersection instanceof Point3D && segment.contains(linex2Intersection)
          ? linex2Intersection
          : null;
      })
      .filter(notNullish)
      .map((intersection) => ({
        point: intersection,
        projection: intersection.subtract(rayStart).dotProduct(rayLine.direction.toPoint(1)),
      }));
    const minPositiveIntersection = minBy(
      raySegmentIntersectionProjections.filter(({ projection }) => distanceGreaterThan(projection, 0)),
      ({ projection }) => projection,
    )?.point;
    const maxNegativeIntersection = maxBy(
      raySegmentIntersectionProjections.filter(({ projection }) => distanceLessThan(projection, 0)),
      ({ projection }) => projection,
    )?.point;
    let dashedSegment;
    if (minPositiveIntersection && !maxNegativeIntersection) {
      dashedSegment = new LineSegment3D(
        minPositiveIntersection,
        minPositiveIntersection.translate(rayLine.direction.toVector(-RAY_LENGTH)),
      );
    } else if (maxNegativeIntersection && !minPositiveIntersection) {
      dashedSegment = new LineSegment3D(
        maxNegativeIntersection,
        maxNegativeIntersection.translate(rayLine.direction.toVector(RAY_LENGTH)),
      );
    } else if (maxNegativeIntersection && minPositiveIntersection) {
      dashedSegment = new LineSegment3D(maxNegativeIntersection, minPositiveIntersection);
    } else if (!lastLine) {
      lines.push(rayLine);
    }

    if (dashedSegment) {
      dashedSegments.push(dashedSegment);
    }
  }

  return { inProgress: true, shape: { polygons: shape ?? [], segments, lines, dashedSegments }, edges: edges ?? [] };
}

/**
 * Cuts a plane into a polygon by bounding it with a series of intersections against other planes. If unsuccessful,
 * attempts to return as many edges as could be calculated in a list, plus a start and end line if applicable.
 * @param cutPlanes The planes used to form the boundary, in order of their intersections.
 */
export function cutPlaneByOrderedPlanes(
  plane: Plane3D,
  cutPlanes: readonly Plane3D[],
  inProgress: boolean,
): BoundedPlaneData {
  return cutPlaneByOrderedPlanesWithOverhangs(
    plane,
    cutPlanes.map((plane) => ({ plane, overhang: null })),
    inProgress,
  );
}

/**
 * Calculates the polygonal boundary of the roof plane as cut by the planes referenced in `cutPlaneGuids`. On failure,
 * returns as many edges as could be calculated, plus a start and end line if applicable.
 */
export function calculateBoundedPlane<T extends RoofOrCeilingPlane>(
  plane: DisplayablePlaneWithCalculatedGeometry<T>,
  allPlanes: ReadonlyRecord<string, PlaneWithCalculatedGeometry<T> | null>,
): BoundedPlaneData & { inProgress: false } {
  const result = calculateBoundedPlaneMaybeInProgress(plane, allPlanes, false);
  if (!result.inProgress) return result;
  throw new Error('Expected result.inProgress to be false');
}

export function calculateBoundedPlaneInProgress<T extends RoofOrCeilingPlane>(
  plane: DisplayablePlaneWithCalculatedGeometry<T>,
  allPlanes: ReadonlyRecord<string, PlaneWithCalculatedGeometry<T> | null>,
): BoundedPlaneData & { inProgress: true } {
  const result = calculateBoundedPlaneMaybeInProgress(plane, allPlanes, true);
  if (result.inProgress) return result;
  throw new Error('Expected result.inProgress to be true');
}

function calculateBoundedPlaneMaybeInProgress<T extends RoofOrCeilingPlane>(
  plane: DisplayablePlaneWithCalculatedGeometry<T>,
  allPlanes: ReadonlyRecord<string, PlaneWithCalculatedGeometry<T> | null>,
  inProgress: boolean,
): BoundedPlaneData {
  const sourceGeometry = convertPlaneToGeometricPlane(plane, 'Bottom');

  const preprocessedPlanes = preprocessPlanesForOverhangs(plane, sourceGeometry, allPlanes);

  return cutPlaneByOrderedPlanesWithOverhangs(sourceGeometry, preprocessedPlanes, inProgress);
}

export const isOverhangSignal = (planeCut: PlaneCut, planeToCutGuid: string) =>
  planeCut.type === 'AgainstPlane' && planeCut.cuttingPlaneGuid === planeToCutGuid;

type OverhangType = 'normal' | 'rake';

/**
 * Given a plane to cut and a cutting plane, determine what kind of overhang could be present. Note that this alone does
 * not determine whether an overhang is really present; we have to look at the neighbors of the cutting plane to know.
 */
function getPossibleOverhangType(planeToCut: Plane3D, planeToCutBy: Plane3D): OverhangType | null {
  const intersection = planeToCut.intersectWithPlane(planeToCutBy);
  // Parallel/coincident planes can't generate overhangs.
  if (!intersection) return null;

  // If the line of intersection is level and the plane is not horizontal, a normal overhang could be present.
  if (
    intersection.direction.isPerpendicularTo(Direction3D.positiveZ) &&
    !planeToCut.halfSpace.direction.isParallelTo(Direction3D.positiveZ)
  )
    return 'normal';

  // In all other cases, a rake overhang is possible and preferred.
  return 'rake';
}

type PlaneCutWithOverhang = { plane: Plane3D | null; overhang: OverhangType | null };

function preprocessPlanesForOverhangs<T extends RoofOrCeilingPlane>(
  plane: DisplayablePlaneWithCalculatedGeometry<T>,
  planeGeometry: Plane3D,
  allPlanes: ReadonlyRecord<string, PlaneWithCalculatedGeometry<T> | null>,
): PlaneCutWithOverhang[] {
  const baseLinePlane = calculateCutPlaneAtBaseLine(plane);

  // Ceiling planes never have overhangs
  if (!isRoofPlane(plane))
    return [baseLinePlane, ...plane.cuts.map((cut) => planeCutToPlane(cut, allPlanes))].map((plane) => ({
      plane,
      overhang: null,
    }));

  // The base line plane always gets an overhang.
  const preprocessed: PlaneCutWithOverhang[] = [
    { plane: baseLinePlane, overhang: getPossibleOverhangType(planeGeometry, baseLinePlane) },
  ];

  let planeInQuestion = null;
  let overhangFlag = false;
  for (const planeToCutBy of [...plane.cuts, 'LAST_SIGNAL'] as const) {
    // Cutting a plane against itself signals an overhang on the most recent non-self plane instead of a physical cut.
    if (planeToCutBy !== 'LAST_SIGNAL' && isOverhangSignal(planeToCutBy, plane.guid)) {
      overhangFlag = true;
      continue;
    }

    // There could be self-cuts before any non-self planes, in that case we bypass appending.
    if (planeInQuestion) {
      // Otherwise, push the plane in question with the appropriate overhang.
      const planeInQuestionGeometry = planeCutToPlane(planeInQuestion, allPlanes);
      const overhang =
        overhangFlag && planeInQuestionGeometry
          ? getPossibleOverhangType(planeGeometry, planeInQuestionGeometry)
          : null;
      preprocessed.push({ plane: planeInQuestionGeometry, overhang });
    }

    // Breaking proves to TypeScript that planeInQuestion will never be read when it is 'LAST_SIGNAL'.
    if (planeToCutBy === 'LAST_SIGNAL') break;

    // Prepare for the next non-signalling plane.
    planeInQuestion = planeToCutBy;
    overhangFlag = false;
  }

  return preprocessed;
}

/**
 * Helper method for calculating subfascia and overhang regions.
 * @param startEndCuts Vertical planes parallel to a horizontal line in `planeToExtend` which define the start and end
 * of the extended region.
 * @param sideCuts Planes which, when intersected with `planeToExtend`, create ridge lines defining the sides of
 * the extended region.
 */
function calculatePlaneExtensionRegion(
  planeToExtend: Plane3D,
  startEndCuts: [Plane3D, Plane3D],
  sideCuts: [Plane3D, Plane3D],
) {
  const startLine = planeToExtend.intersectWithPlane(startEndCuts[0]);
  const side0CutLine = planeToExtend.intersectWithPlane(sideCuts[0]);
  const side1CutLine = planeToExtend.intersectWithPlane(sideCuts[1]);
  const endLine = planeToExtend.intersectWithPlane(startEndCuts[1]);
  if (!startLine || !side0CutLine || !side1CutLine || !endLine) return null;

  const intersection1 = startLine.intersectWithLine(side0CutLine);
  const intersection2 = side0CutLine.intersectWithLine(endLine);
  const intersection3 = endLine.intersectWithLine(side1CutLine);
  const intersection4 = side1CutLine.intersectWithLine(startLine);
  if (
    !(intersection1 instanceof Point3D) ||
    !(intersection2 instanceof Point3D) ||
    !(intersection3 instanceof Point3D) ||
    !(intersection4 instanceof Point3D)
  )
    return null;

  const intersections = [intersection1, intersection2, intersection3, intersection4];

  // Disallow degenerate regions. Also disallows triangular overhangs and subfasciae, which may change one day.
  if (circularPairwise(intersections).some(([i1, i2]) => i1.equals(i2))) return null;

  try {
    return new Polygon3D(intersections);
  } catch {
    return null;
  }
}

function verticalPlaneFromSegment({ basePoint, endPoint }: LineSegment3D): Plane3D {
  if (basePoint.equals(endPoint)) throw new Error('Cannot create unique vertical plane from vertical segment');
  return Plane3D.fromPoints(basePoint, endPoint, basePoint.translate(Direction3D.positiveZ.toVector(1)))!;
}

export function calculateOverhangsAndSubfasciaeForBoundedPlane(
  plane: RoofPlane,
  planeData: BoundedPlaneData,
): {
  overhangs: Polygon3D[] | null;
  subfasciae: Polygon3D[] | null;
} {
  const { edges, shape } = planeData;
  const polygons = 'polygons' in shape ? shape.polygons : shape;
  if (polygons.length === 0) return { overhangs: null, subfasciae: null };

  const asPlane = polygons[0].asPlane();
  const { overhang: overhangDistance, subfasciaThickness } = plane;
  const overhangTail = overhangDistance - subfasciaThickness;

  if (
    [overhangDistance, overhangTail, subfasciaThickness].some((dimension) => distanceLessThan(dimension, 0)) ||
    // Vertical planes cannot be overhung
    angleEquals(plane.slopeInDegrees, 90)
  )
    return { overhangs: null, subfasciae: null };

  const edgesWithOverhangDirections = edges.map(({ edge, overhang }) => {
    if (!overhang) return { edge, overhangData: null };

    const outerDirection = edge.direction.crossProductDirection(Direction3D.positiveZ)!;
    return { edge, overhangData: { overhang, overhangDirection: outerDirection } };
  });

  const regions = circularTripletwise(edgesWithOverhangDirections)
    .map(([previousEdge, { edge, overhangData }, nextEdge]) => {
      if (!overhangData) return null;
      const { overhang, overhangDirection } = overhangData;

      const calculateSidePlanes = (neighbor: typeof previousEdge) => {
        const sharedPoint = edge.endPoints.filter((endPoint) =>
          neighbor.edge.endPoints.some(endPoint.equals.bind(endPoint)),
        )[0];
        if (neighbor.edge.direction.equals(edge.direction)) {
          // Two collinear neighboring edges can't be used to slice overhangs for each other, so we go orthogonally.
          const orthogonalDirection = edge.direction
            .crossProductDirection(Direction3D.positiveZ)!
            .orientWith(overhangDirection);
          const orthogonalSegment = new LineSegment3D(
            sharedPoint,
            sharedPoint.translate(orthogonalDirection.toVector(1)),
          );
          return { plane: verticalPlaneFromSegment(orthogonalSegment) };
        }

        if (overhang === 'normal' || !neighbor.overhangData) {
          const neighborRakeSubfasciaExtension =
            neighbor.overhangData?.overhang === 'rake'
              ? verticalPlaneFromSegment(
                  neighbor.edge.translate(neighbor.overhangData.overhangDirection.toVector(overhangDistance)),
                )
              : null;
          return { plane: verticalPlaneFromSegment(neighbor.edge), neighborRakeSubfasciaExtension };
        }

        if (neighbor.overhangData.overhang === 'normal')
          return {
            plane: verticalPlaneFromSegment(
              neighbor.edge.translate(neighbor.overhangData.overhangDirection.toVector(overhangTail)),
            ),
          };

        if (neighbor.overhangData.overhang === 'rake') {
          // Two rake overhangs next to each other at a corner must be mitered together.
          const translation = edge.direction
            .projectTo2D()
            .toVector(1)
            .subtract(neighbor.edge.direction.projectTo2D().toVector(1))
            .to3D();
          const miteredSegment = new LineSegment3D(sharedPoint, sharedPoint.translate(translation));
          return { plane: verticalPlaneFromSegment(miteredSegment) };
        }

        return assertExhaustiveSwitch(neighbor.overhangData.overhang);
      };

      const { plane: leftSidePlane, neighborRakeSubfasciaExtension: leftSideSubfasciaPlane } =
        calculateSidePlanes(previousEdge);
      const { plane: rightSidePlane, neighborRakeSubfasciaExtension: rightSideSubfasciaPlane } =
        calculateSidePlanes(nextEdge);
      const roofEdge = verticalPlaneFromSegment(edge);
      const overhangTailEdge = verticalPlaneFromSegment(edge.translate(overhangDirection.toVector(overhangTail)));
      const subfasciaEdge = verticalPlaneFromSegment(edge.translate(overhangDirection.toVector(overhangDistance)));

      const overhangTailRegion = calculatePlaneExtensionRegion(
        asPlane,
        [roofEdge, overhangTailEdge],
        [leftSidePlane, rightSidePlane],
      );
      if (!overhangTailRegion) return null;

      const subfasciaRegions = calculatePlaneExtensionRegion(
        asPlane,
        [overhangTailEdge, subfasciaEdge],
        [leftSideSubfasciaPlane ?? leftSidePlane, rightSideSubfasciaPlane ?? rightSidePlane],
      );

      return { overhang: overhangTailRegion, subfascia: subfasciaRegions };
    })
    .filter(notNullish);

  const overhangs = regions.map(({ overhang }) => overhang);
  const subfasciae = regions.map(({ subfascia }) => subfascia).filter(notNullish);

  return { overhangs, subfasciae };
}

interface LineSegmentWithElevation {
  segment: LineSegment2D;
  elevation: number;
}

const getHorizontalSegmentsOfCeilingPlane = (plane: Polygon3D): LineSegmentWithElevation[] =>
  plane.edges
    .filter(({ basePoint, endPoint }) => distanceEquals(basePoint.z, endPoint.z))
    .map((edge) => ({ segment: edge.projectTo2D()!, elevation: edge.midpoint.z }));

const getHorizontalSegmentsOfBearingEnvelope = (envelope: BearingEnvelope): LineSegmentWithElevation[] => {
  const footprint = calculateBearingEnvelopeShape(envelope);
  return footprint ? footprint.edges.map((segment) => ({ segment, elevation: envelope.top })) : [];
};

/**
 * Infers horizontal ceiling plane polygons by tracing along the outsides of bearing envelopes and the horizontal edges
 * of bound ceiling planes.
 */
export function calculateInferredCeilingPlanes(
  ceilingPlanes: readonly Polygon3D[],
  bearingEnvelopes: readonly BearingEnvelope[],
): Polygon3D[] {
  const ceilingSegments = ceilingPlanes.flatMap(getHorizontalSegmentsOfCeilingPlane);
  const bearingSegments = bearingEnvelopes.flatMap(getHorizontalSegmentsOfBearingEnvelope);
  const allSegments = ceilingSegments.concat(bearingSegments);

  // Group segments by elevation and by transitive connection
  const segmentsGroupedByElevation = groupTransitivelyByDistanceEquality(allSegments, ({ elevation }) => elevation);
  const segmentsGroupedByElevationAndConnection = segmentsGroupedByElevation.flatMap((group) =>
    groupTransitively(
      group,
      ({ segment: segment1 }, { segment: segment2 }) =>
        segment1.overlaps(segment2) || !!segment1.intersectWithSegment(segment2),
    ),
  );

  return segmentsGroupedByElevationAndConnection.flatMap((group) => {
    const { elevation } = group[0];
    const segments = group.map(({ segment }) => segment);
    return createDisjointRoofBasePolygonFromEdges(segments).map((polygon) =>
      polygon.to3D(elevation).orientWith({ normalDirection: Direction3D.in }),
    );
  });
}
